import { useContext } from 'react';
import { QueryClient, QueryClientContext } from '@tanstack/react-query';
import { useGlobalStore } from '../../stores/globalStore';
import { useMapThisCount } from '../../api/ehrMapThisApi';
import { useAllergyChangeCount } from '../../api/ehrAllergyReviewApi';
import { useAddedItemsCount } from '../../api/ehrAddedItemsApi';
import type { MapThisCount } from '../../types/ehrMapThis';

// The EHR area's two extra badges: text waiting for mapping (EHR / Pending Messages in the
// navigation, and the Mappings tab and page) and allergy changes waiting for review (the
// Allergy Changes tab). Each is one small cached request every two minutes, made only for
// someone who can open the EHR pages at a facility whose EHR is on.

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

// The sidebar can render outside a QueryClientProvider (its own tests do): then the badges stay off,
// as readyReports does, instead of throwing.
const detachedClient = new QueryClient();
function useBadgeClient(): { client: QueryClient; present: boolean } {
  const context = useContext(QueryClientContext);
  return { client: context ?? detachedClient, present: !!context };
}

/** Dietech_Ehr_Level above 0 at a facility whose EHR is on (the same gate as the Action Needed badge). */
export function useCanSeeEhr(): boolean {
  return useGlobalStore((s) =>
    (s.userRole?.dietech_Ehr_Level ?? 0) > 0
    && !!(s.facilitySettings as Record<string, unknown> | null)?.facilityEhrActive);
}

/**
 * The text waiting for mapping at the current facility, or null. Only for a role that can see the
 * Mappings tab (a diet order or allergy mapping level above 0): the people who can do something about it.
 */
export function useWaitingTextCount(): MapThisCount | null {
  const facilityId = useGlobalStore((s) => s.facilityId);
  const canSeeEhr = useCanSeeEhr();
  const canSeeMappings = useGlobalStore((s) =>
    (s.userRole?.dietech_Ehr_Diet_Order_Mapping_Level ?? 0) > 0
    || (s.userRole?.dietech_Ehr_Allergy_Mapping_Level ?? 0) > 0);
  const { client, present } = useBadgeClient();
  const enabled = present && canSeeEhr && canSeeMappings;
  const q = useMapThisCount(facilityId, enabled, client);
  return enabled && q.data ? q.data : null;
}

/** The allergy changes waiting for review at the current facility (0 when there are none or the list is not set up). */
export function useAllergyReviewCount(): number {
  const facilityId = useGlobalStore((s) => s.facilityId);
  const canSeeEhr = useCanSeeEhr();
  const { client, present } = useBadgeClient();
  const enabled = present && canSeeEhr;
  const q = useAllergyChangeCount(facilityId, enabled, client);
  return enabled && q.data?.available ? q.data.pending : 0;
}

/** What the EHR gave or ended for residents, waiting for review (0 when there is nothing or the list is not set up). */
export function useAddedItemsReviewCount(): number {
  const facilityId = useGlobalStore((s) => s.facilityId);
  const canSeeEhr = useCanSeeEhr();
  const { client, present } = useBadgeClient();
  const enabled = present && canSeeEhr;
  const q = useAddedItemsCount(facilityId, enabled, client);
  return enabled && q.data?.available ? q.data.pending : 0;
}

/** "3 phrases to map" / "1 phrase to map": the waiting text badge's spoken label. */
export function waitingTextLabel(count: number): string {
  return `${plural(count, 'phrase', 'phrases')} to map`;
}

/** The Mappings tab badge: the phrases holding messages first, otherwise all of them. */
export function waitingTextBadge(c: MapThisCount | null): { text: string; spoken: string; alert: boolean; title: string } | null {
  if (!c || typeof c.total !== 'number' || c.total <= 0) return null;
  const holding = c.holding > 0;
  const title = holding
    ? `${plural(c.holding, 'phrase is', 'phrases are')} holding ${plural(c.heldMessages, 'message', 'messages')}`
      + (c.notHolding > 0 ? `, and ${c.notHolding} more ${c.notHolding === 1 ? 'was' : 'were'} sent but not applied` : '')
    : `${plural(c.notHolding, 'phrase was', 'phrases were')} sent but not applied`;
  return { text: String(holding ? c.holding : c.total), spoken: waitingTextLabel(holding ? c.holding : c.total), alert: holding, title };
}

/** The waiting text banner on the Mappings page. */
export function waitingTextBanner(c: MapThisCount): string {
  const head = `${plural(c.total, 'phrase is', 'phrases are')} waiting for mapping`;
  if (c.holding === 0) return `${head}. They were sent but not applied, so nothing is held.`;
  const allergy = c.holdingAllergy > 0 ? ` (${plural(c.holdingAllergy, 'allergy', 'allergies')})` : '';
  return `${head}: ${c.holding}${allergy} ${c.holding === 1 ? 'is' : 'are'} holding ${plural(c.heldMessages, 'message', 'messages')}.`;
}

/** "2 to review": the Allergy Changes badge's spoken label. */
export function allergyReviewLabel(count: number): string {
  return `${count} to review`;
}
